import { ConfirmDialog } from "../../../../components/ui";
import { dateRange, periodName, shortDate, wasReturned } from "./hrPayroll";

/**
 * Calculate / Recalculate payroll (C3: payroll-affecting, many employees). What the server does
 * (payroll.runtime.service computePeriod), in one transaction:
 *  - refuses unless the period is being prepared or is calculated and not yet with Finance;
 *  - re-derives approved overtime for the dates, then deletes this period's results and creates them again from
 *    the current attendance, leave, overtime, pay, deductions and government rules;
 *  - never changes a loan balance (only a confirmed salary payment does), never pays anyone, and employees do not
 *    see payslips until HR releases them after Finance approves.
 * A recalculation keeps HR's reviewed government amounts only where the figures they were based on are unchanged.
 */
export default function CalculatePayrollDialog({ period, recalculate = false, employeeCount = null, onConfirm, onClose }) {
  const name = periodName(period) || "this period";
  const facts = [
    { label: "Dates", value: dateRange(period) || "Not set" },
    ...(period?.pay_date ? [{ label: "Pay date", value: shortDate(period.pay_date) }] : []),
    ...(employeeCount != null ? [{ label: "Employees", value: String(employeeCount) }] : []),
  ];
  const returned = wasReturned(period);

  return (
    <ConfirmDialog
      size="md"
      title={recalculate ? `Recalculate payroll for ${name}?` : `Calculate payroll for ${name}?`}
      facts={facts}
      consequence={recalculate
        ? `The current results are replaced with new ones worked out from the latest attendance, leave, overtime, pay and deductions, so amounts may change. Reviewed government amounts are kept only where the figures they were based on did not change.${returned ? " Check the results, then send it to Finance again." : " Check the results before sending it to Finance."}`
        : "Works out each employee's pay, deductions, government contributions and withholding tax from the attendance, leave, overtime, pay and deductions recorded now. Nobody is paid and employees do not see it yet: you check the results, then send them to Finance."}
      confirmLabel={recalculate ? "Recalculate payroll" : "Calculate payroll"}
      failureTitle={recalculate ? "Payroll wasn't recalculated" : "Payroll wasn't calculated"}
      errorFallback="The payroll couldn't be calculated. Try again."
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
